import MiniSearch from "minisearch";
import type { SearchOptions, SearchResult } from "minisearch";

const MIN_PREFIX_LENGTH = 3;

// Only the last word may still be mid-typing, so only it prefix-matches, and only once it's long enough.
function isPrefixEligible(term: string, index: number, terms: string[]): boolean {
  return index === terms.length - 1 && term.length >= MIN_PREFIX_LENGTH;
}

export const FILTER_OPTIONS = { combineWith: "AND", prefix: isPrefixEligible } as const;

export function tokenizeSearchText(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter((token) => token.length > 0);
}

/**
 * Drops any term that's still too short to be a meaningful prefix, instead of
 * requiring it to match anything — "he touch" and "touch he" both resolve to
 * "touch" regardless of where the short word falls in the query. Falls back
 * to the original terms when every term is under the floor, since dropping
 * all of them would leave nothing to search on.
 */
function dropShortTerms(terms: string[]): string[] {
  if (terms.length <= 1) return terms;
  const keptTerms = terms.filter((term) => term.length >= MIN_PREFIX_LENGTH);
  return keptTerms.length > 0 ? keptTerms : terms;
}

export function resolveSearchQuery(rawQuery: string): string {
  return dropShortTerms(tokenizeSearchText(rawQuery)).join(" ");
}

export interface SearchWithFallbackResult {
  results: SearchResult[];
  resolvedQuery: string;
}

/**
 * Filters with the full query first, since dropping a short word is only
 * needed when it truly is an incomplete fragment; falling back only on a
 * zero-result full query avoids discarding a short word (e.g. "he") that's
 * a real, present word in the indexed documents.
 */
export function searchWithWordDropFallback(
  index: MiniSearch,
  rawQuery: string,
  options: SearchOptions
): SearchWithFallbackResult {
  const terms = tokenizeSearchText(rawQuery);
  const fullQuery = terms.join(" ");
  const fullResults = index.search(fullQuery, options);
  if (fullResults.length > 0) return { results: fullResults, resolvedQuery: fullQuery };

  const droppedQuery = dropShortTerms(terms).join(" ");
  if (droppedQuery === fullQuery) return { results: fullResults, resolvedQuery: fullQuery };

  return { results: index.search(droppedQuery, options), resolvedQuery: droppedQuery };
}
